import React from "react";
import ReactDOM from "react-dom/client";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "./index.css";
import App from "./App";
import { bootstrapTheme } from "./lib/theme";
import { runWhenIdle } from "./lib/call-guard";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

// Debounce the global ResizeObserver so its callback never fires twice in the
// same animation frame. Without this, any observer (e.g. @xyflow/react's
// internal viewport tracking) reacting to a layout change twice in one tick
// throws the browser's benign "ResizeObserver loop completed with undelivered
// notifications" warning -- harmless in production, but CRA's dev-only error
// overlay treats it as fatal and blocks the whole screen with it. Must run
// before any component (e.g. React Flow) constructs its own observer.
if (typeof window !== "undefined" && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class DebouncedResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      let frame = 0;
      super((...args) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => callback(...args));
      });
    }
  };
}

// Applied before the first render so fullscreen routes that sit outside
// AppLayout (the workflow studio) still come up in the saved theme.
bootstrapTheme();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Registers the service worker in production builds only (CRA no-ops this
// in dev). Enables "Install app" + offline app-shell caching — see
// serviceWorkerRegistration.js.
//
// CRA's default behavior deliberately waits for every open tab to close
// before a newly-fetched build activates — reasonable for a site people
// close and reopen, but an installed PWA is often never fully closed, so it
// would keep serving the version from whenever it was first installed
// indefinitely, "fixed" only by force-quitting the app. Skip that wait and
// swap in the new build as soon as it's ready, then reload once so the tab
// picks it up immediately instead of needing a manual refresh.
//
// Never mid-call, though: a hard reload tears down the LiveKit room and
// drops the operator's WebRTC audio instantly, no warning — exactly what
// happened before this guard existed. runWhenIdle defers the reload until
// call-guard.ts reports nothing's connected (manual call, supervisor
// listen/takeover — see use-call-audio.ts), firing right away if none is.
let refreshingForUpdate = false;
navigator.serviceWorker?.addEventListener("controllerchange", () => {
  if (refreshingForUpdate) return;
  refreshingForUpdate = true;
  runWhenIdle(() => window.location.reload());
});

serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    registration.waiting?.postMessage({ type: "SKIP_WAITING" });
  },
});